import { PLAY_STORE_PACKAGE } from '@/utils/config';
import { Tracker } from '@/utils';
import { isProductionEnv } from '@/utils/getEnv';

export const IOS_APP_STORE_APP_ID = '6769384550';
export const IOS_APP_STORE_SLUG = 'ahacreator';

export type DownloadDevice = 'ios' | 'android' | 'unknown';

export interface DownloadAttribution {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  banner_source: string;
  banner_id: string;
  click_id: string;
  user_id: string;
}

export const DEFAULT_SMART_LINK_ATTRIBUTION: Omit<
  DownloadAttribution,
  'click_id' | 'user_id'
> = {
  utm_source: 'download_smart_link',
  utm_medium: 'smart_link',
  utm_campaign: 'app_download',
  banner_source: 'download_smart_link',
  banner_id: 'download_smart_link'
};

export function createDownloadClickId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/** 从落地页 / 二维码 URL 查询串合并归因（保留外部传入的 utm 等参数） */
export function buildAttributionFromSearch(
  search: string,
  overrides?: Partial<DownloadAttribution>
): DownloadAttribution {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  );

  const pick = (key: keyof DownloadAttribution, fallback: string): string => {
    if (overrides && key in overrides && overrides[key] !== undefined) {
      return overrides[key] as string;
    }
    const fromParams = params.get(key);
    if (fromParams !== null) return fromParams;
    return fallback;
  };

  return {
    utm_source: pick('utm_source', DEFAULT_SMART_LINK_ATTRIBUTION.utm_source),
    utm_medium: pick('utm_medium', DEFAULT_SMART_LINK_ATTRIBUTION.utm_medium),
    utm_campaign: pick(
      'utm_campaign',
      DEFAULT_SMART_LINK_ATTRIBUTION.utm_campaign
    ),
    banner_source:
      pick('banner_source', '') ||
      params.get('banner_source') ||
      params.get('utm_source') ||
      DEFAULT_SMART_LINK_ATTRIBUTION.banner_source,
    banner_id: pick('banner_id', DEFAULT_SMART_LINK_ATTRIBUTION.banner_id),
    click_id:
      overrides?.click_id ?? params.get('click_id') ?? createDownloadClickId(),
    user_id: pick('user_id', '')
  };
}

export function detectDownloadDevice(userAgent: string): DownloadDevice {
  const ua = userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'unknown';
}

export function isWeChatInAppBrowser(userAgent: string): boolean {
  return /MicroMessenger/i.test(userAgent);
}

/** 中国大陆及中文环境优先使用 /cn/ App Store 链接 */
export function shouldUseChinaAppStore(): boolean {
  if (typeof navigator === 'undefined') return true;
  const lang = (navigator.language || '').toLowerCase();
  if (
    lang.startsWith('zh') ||
    lang.includes('cn') ||
    lang.includes('hans') ||
    lang.includes('hant')
  ) {
    return true;
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (/Asia\/(Shanghai|Hong_Kong|Taipei|Macau|Urumqi)|PRC/i.test(tz)) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

export function buildIosAppStoreUrl(
  useChina = shouldUseChinaAppStore()
): string {
  if (useChina) {
    return `https://apps.apple.com/cn/app/${IOS_APP_STORE_SLUG}/id${IOS_APP_STORE_APP_ID}`;
  }
  return `https://apps.apple.com/app/id${IOS_APP_STORE_APP_ID}`;
}

export function buildPlayStoreUrl(attribution: DownloadAttribution): string {
  const referrer = new URLSearchParams({
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: attribution.utm_campaign,
    banner_source: attribution.banner_source,
    banner_id: attribution.banner_id,
    click_id: attribution.click_id,
    user_id: attribution.user_id
  }).toString();

  return `https://play.google.com/store/apps/details?id=${PLAY_STORE_PACKAGE}&referrer=${encodeURIComponent(referrer)}`;
}

export function getDownloadLandingPath(search = ''): string {
  if (!search) return '/download';
  return `/download${search.startsWith('?') ? search : `?${search}`}`;
}

export function getDownloadLandingUrl(origin?: string, search = ''): string {
  const base = (
    origin || (typeof window !== 'undefined' ? window.location.origin : '')
  ).replace(/\/$/, '');
  return `${base}${getDownloadLandingPath(search)}`;
}

export const PRODUCTION_DOWNLOAD_LANDING_URL = getDownloadLandingUrl(
  'https://creator.ahacreator.com',
  '?utm_source=qr&utm_medium=smart_link&utm_campaign=app_download'
);

export const STAGING_DOWNLOAD_LANDING_URL = getDownloadLandingUrl(
  'https://test.creator.ahacreator.com',
  '?utm_source=qr&utm_medium=smart_link&utm_campaign=app_download'
);

/** Navbar / 物料用二维码 PNG（随 REACT_APP_ENV 切换） */
export function getDownloadAppQrImagePath(): string {
  const file = isProductionEnv()
    ? '/download-app-qr.png'
    : '/download-app-qr-test.png';
  const publicUrl = process.env.PUBLIC_URL;
  if (!publicUrl) return file;
  return `${publicUrl.replace(/\/$/, '')}${file}`;
}

export type DownloadStoreTarget = 'android' | 'ios';

export function trackDownloadStoreClick(
  target: DownloadStoreTarget,
  attribution: DownloadAttribution,
  extra?: Record<string, string> & { event_category?: string }
) {
  if (typeof window === 'undefined') return;
  const { event_category: eventCategory, ...rest } = extra || {};
  Tracker.gtag('event', 'click', {
    event_category: eventCategory || 'download_smart_link_click',
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: attribution.utm_campaign,
    banner_id: attribution.banner_id,
    banner_source: attribution.banner_source,
    click_id: attribution.click_id,
    user_id: attribution.user_id,
    type: target,
    ...rest
  });
}

export function trackDownloadLandingView(
  attribution: DownloadAttribution,
  meta: { device: DownloadDevice; mode: 'redirect' | 'chooser' | 'wechat' }
) {
  if (typeof window === 'undefined') return;
  Tracker.gtag('event', 'screen_view', {
    event_category: 'download_smart_link_view',
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: attribution.utm_campaign,
    banner_id: attribution.banner_id,
    click_id: attribution.click_id,
    device: meta.device,
    mode: meta.mode
  });
}
